import { useEffect, useMemo, useState } from "react";
import { http } from "../api";
import { ArrowLeft, Save, Trash2, Eye, Download, Printer, User, MapPin, Phone, IdCard, X, FileText, GitBranch, Layers, Activity, AlertTriangle, Compass, Calendar, Heart, MessageSquare, Building2, Vote, ScrollText } from "lucide-react";
import { PageHeader, EmptyState, PersonLink } from "../shared";
import VoterJourney from "./VoterJourney";

// ---------- Activity feed (rich merged timeline) --------------------------
const ACTIVITY_ICON = {
  complaint: AlertTriangle,
  field_visit: Compass,
  event_rsvp: Calendar,
  turnout: Vote,
  donation: Heart,
  chat_mention: MessageSquare,
  org_membership: Building2,
  audit: ScrollText,
};

const ACTIVITY_LABEL = {
  complaint: "Complaint",
  field_visit: "Field visit",
  event_rsvp: "Event RSVP",
  turnout: "Turnout",
  donation: "Donation",
  chat_mention: "Chat mention",
  org_membership: "Organization",
  audit: "Edit",
};

function ActivityFeed({ contactName, timeline, audit, filter, onFilter }) {
  const auditItems = (audit || [])
    .filter((a) => (a.entity === "contact" || a.entity === "voter"))
    .map((a) => ({
      when: a.created_at,
      type: "audit",
      title: `${(a.action || "").replace(/^./, (s) => s.toUpperCase())} — ${a.entity}`,
      meta: `${a.user_name}${a.meta && Object.keys(a.meta).length ? " · " + JSON.stringify(a.meta).slice(0, 90) : ""}`,
    }));
  const merged = [...(timeline || []), ...auditItems].sort(
    (a, b) => String(b.when || "").localeCompare(String(a.when || ""))
  );

  const counts = merged.reduce((acc, r) => { acc[r.type] = (acc[r.type] || 0) + 1; return acc; }, {});
  const types = ["all", ...Object.keys(counts)];
  const filtered = filter === "all" ? merged : merged.filter((r) => r.type === filter);

  if (merged.length === 0) {
    return <EmptyState icon={Activity} title="No activity yet" hint={`Complaints, field visits, calls, event RSVPs and edits for ${contactName} will show up here.`} />;
  }

  return (
    <>
      <div className="pp-activity-filters" data-testid="pp-activity-filters">
        {types.map((t) => (
          <button
            key={t}
            className={`chip-filter ${filter === t ? "active" : ""}`}
            data-testid={`pp-activity-filter-${t}`}
            onClick={() => onFilter(t)}
          >
            {t === "all" ? `All (${merged.length})` : `${ACTIVITY_LABEL[t] || t} (${counts[t]})`}
          </button>
        ))}
      </div>
      <ul className="pp-activity-list" data-testid="pp-activity-list">
        {filtered.map((r, i) => {
          const Icon = ACTIVITY_ICON[r.type] || Activity;
          const when = r.when ? new Date(r.when).toLocaleString() : "";
          return (
            <li key={i} className="pp-activity-row" data-testid={`pp-activity-row-${i}`}>
              <span className={`pp-activity-icon ${r.type}`}><Icon size={16} /></span>
              <span className="pp-activity-body">
                <b>{r.title}</b>
                {r.meta && <small>{r.meta}</small>}
                <span className="pp-activity-when">{when}</span>
              </span>
            </li>
          );
        })}
        {filtered.length === 0 && <li className="hint" style={{ padding: 12 }}>No {ACTIVITY_LABEL[filter] || filter} entries.</li>}
      </ul>
    </>
  );
}

// ---------- Editable field ------------------------------------------------
function EditableField({ label, value, onSave, type = "text", options, testid, placeholder = "—", disabled }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (!editing) setDraft(value ?? ""); }, [value, editing]);

  const commit = async () => {
    if (String(draft) === String(value ?? "")) { setEditing(false); return; }
    setSaving(true); setError("");
    try {
      await onSave(draft === "" ? null : draft);
      setEditing(false);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Save failed");
    } finally { setSaving(false); }
  };

  const cancel = () => { setDraft(value ?? ""); setEditing(false); setError(""); };
  const shown = value === undefined || value === null || value === "" ? placeholder : String(value);

  return (
    <div className="pp-field" data-testid={testid}>
      <span className="pp-field-label">{label}</span>
      {editing ? (
        <div className="pp-field-edit">
          {type === "select" ? (
            <select value={draft ?? ""} onChange={(e) => setDraft(e.target.value)} autoFocus>
              <option value="">— none —</option>
              {(options || []).map((o) => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}
            </select>
          ) : type === "textarea" ? (
            <textarea value={draft ?? ""} onChange={(e) => setDraft(e.target.value)} autoFocus rows={3} />
          ) : (
            <input
              type={type}
              value={draft ?? ""}
              autoFocus
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && type !== "textarea") commit(); if (e.key === "Escape") cancel(); }}
            />
          )}
          <div className="pp-field-actions">
            <button className="pp-btn-primary" onClick={commit} disabled={saving} data-testid={`${testid}-save`}>
              {saving ? "…" : "Save"}
            </button>
            <button className="pp-btn-ghost" onClick={cancel} data-testid={`${testid}-cancel`}>Cancel</button>
          </div>
          {error && <small className="pp-field-error">{error}</small>}
        </div>
      ) : (
        <span
          className={`pp-field-value ${!value ? "pp-field-blank" : ""} ${disabled ? "pp-field-locked" : ""}`}
          onClick={() => { if (!disabled) setEditing(true); }}
          data-testid={`${testid}-value`}
        >{shown}</span>
      )}
    </div>
  );
}

// ---------- Root ----------------------------------------------------------
export default function Profile({ user, contactId, onBack }) {
  const isSuper = user?.role === "Super Admin";
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("details");
  const [pages, setPages] = useState([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [error, setError] = useState("");
  const [audit, setAudit] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [activityFilter, setActivityFilter] = useState("all");
  const [households, setHouseholds] = useState([]);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const r = await http.get(`/contacts/${contactId}`);
      setContact(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load contact");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (contactId) load(); }, [contactId]);
  useEffect(() => { http.get("/households").then((r) => setHouseholds(r.data || [])).catch(() => {}); }, []);

  useEffect(() => {
    if (tab !== "roll" || !contactId) return;
    setPagesLoading(true);
    http.get(`/contacts/${contactId}/voter-roll-pages`)
      .then((r) => setPages(r.data.pages || []))
      .catch(() => setPages([]))
      .finally(() => setPagesLoading(false));
  }, [tab, contactId]);

  useEffect(() => {
    if (tab !== "activity" || !contactId) return;
    setTimelineLoading(true);
    Promise.all([
      http.get(`/voters/${contactId}/timeline`).then((r) => r.data).catch(() => ({ timeline: [] })),
      http.get(`/audit?entity_id=${contactId}&limit=50`).then((r) => r.data).catch(() => []),
    ]).then(([tl, au]) => {
      setTimeline(tl.timeline || []);
      setAudit(au || []);
    }).finally(() => setTimelineLoading(false));
  }, [tab, contactId]);

  const patch = async (fieldChanges) => {
    if (!contact) return;
    const nextPayload = { ...contact, ...fieldChanges };
    // The backend PATCH accepts a full ContactIn body — strip server-managed fields
    delete nextPayload.contact_id;
    delete nextPayload.created_at;
    delete nextPayload.created_by;
    delete nextPayload.updated;
    delete nextPayload.class_last_updated;
    const r = await http.patch(`/contacts/${contact.contact_id}`, nextPayload);
    setContact(r.data);
  };

  const deleteContact = async () => {
    if (!window.confirm(`Move "${contact?.name}" to the recycle bin?`)) return;
    try {
      await http.delete(`/contacts/${contact.contact_id}`);
      if (onBack) onBack();
    } catch (e) { alert(e.response?.data?.detail || "Delete failed"); }
  };

  const householdName = useMemo(() => {
    if (!contact?.household_id) return null;
    const h = households.find((x) => x.household_id === contact.household_id);
    return h ? (h.family_head || h.head_name || h.household_id) : contact.household_id;
  }, [contact, households]);

  if (loading) return <div className="hint" style={{ padding: 32 }}>Loading profile…</div>;
  if (error || !contact) return (
    <>
      <PageHeader eyebrow="PROFILE" title="Person not found" subtitle={error || "This contact may have been deleted."}
        actions={<button className="outline" data-testid="profile-back-error" onClick={onBack}><ArrowLeft size={14} /> Back to People</button>} />
    </>
  );

  const genderOptions = ["Male", "Female", "Other", "Unknown"];
  const inclinationOptions = ["Supporter", "Neutral", "Opposition", "Undecided"];
  const statusOptions = ["Verified", "Incomplete", "Do-Not-Contact"];
  const guardianRelationOptions = ["Father", "Mother", "Husband", "Guardian"];
  const langOptions = ["auto", "en", "bn"];

  return (
    <>
      <PageHeader
        eyebrow="PROFILE"
        title={contact.name}
        subtitle={
          <span data-testid="profile-subtitle">
            {contact.gender ? `${contact.gender} · ` : ""}
            {contact.age ? `Age ${contact.age} · ` : ""}
            {contact.ward != null ? `Ward ${contact.ward}` : ""}
            {contact.booth ? ` · Booth ${contact.booth}` : ""}
            {contact.epic ? ` · EPIC ${contact.epic}` : ""}
          </span>
        }
        actions={
          <div className="pp-header-actions">
            <button className="outline" data-testid="profile-back" onClick={onBack}><ArrowLeft size={14} /> Back</button>
            {isSuper && (
              <button className="danger" data-testid="profile-delete" onClick={deleteContact}><Trash2 size={14} /> Delete</button>
            )}
          </div>
        }
      />

      <div className="pp-tabs" data-testid="pp-tabs">
        <button className={tab === "details" ? "on" : ""} data-testid="pp-tab-details" onClick={() => setTab("details")}><User size={13} /> Details</button>
        <button className={tab === "roll" ? "on" : ""} data-testid="pp-tab-roll" onClick={() => setTab("roll")}><Layers size={13} /> Voter roll ({pages.length || (contact.voter_roll_page_file_id ? "1+" : "0")})</button>
        <button className={tab === "journey" ? "on" : ""} data-testid="pp-tab-journey" onClick={() => setTab("journey")}><GitBranch size={13} /> Journey</button>
        <button className={tab === "activity" ? "on" : ""} data-testid="pp-tab-activity" onClick={() => setTab("activity")}><Activity size={13} /> Activity</button>
      </div>

      {tab === "details" && (
        <section className="pp-details" data-testid="pp-details">
          <div className="pp-panel">
            <div className="pp-panel-head"><IdCard size={14} /> <b>Identity</b></div>
            <EditableField testid="pp-name" label="Full name" value={contact.name} onSave={(v) => patch({ name: v })} />
            <EditableField testid="pp-gender" label="Gender" value={contact.gender} type="select" options={genderOptions} onSave={(v) => patch({ gender: v })} />
            <EditableField testid="pp-age" label="Age" value={contact.age} type="number" onSave={(v) => patch({ age: v })} />
            <EditableField testid="pp-dob" label="Date of birth" value={contact.dob} type="date" onSave={(v) => patch({ dob: v })} />
            <EditableField testid="pp-blood" label="Blood group" value={contact.blood_group} onSave={(v) => patch({ blood_group: v })} />
            <EditableField testid="pp-lang" label="Preferred language" value={contact.preferred_language || "auto"} type="select" options={langOptions} onSave={(v) => patch({ preferred_language: v })} />
          </div>

          <div className="pp-panel">
            <div className="pp-panel-head"><Phone size={14} /> <b>Contact</b></div>
            <EditableField testid="pp-mobile" label="Mobile" value={contact.mobile} onSave={(v) => patch({ mobile: v })} />
            <EditableField testid="pp-whatsapp" label="WhatsApp" value={contact.whatsapp} onSave={(v) => patch({ whatsapp: v })} />
            <EditableField testid="pp-address" label="Address" value={contact.address} type="textarea" onSave={(v) => patch({ address: v })} />
            <EditableField testid="pp-profession" label="Profession" value={contact.profession} onSave={(v) => patch({ profession: v })} />
            <EditableField testid="pp-guardian-name" label="Guardian's name" placeholder="Father / Mother / Husband — from voter roll" value={contact.guardian_name} onSave={(v) => patch({ guardian_name: v })} />
            {contact.guardian_contact_id && (
              <div className="pp-field-hint" data-testid="pp-guardian-openlink" style={{ marginTop: -8, marginLeft: 12, fontSize: 11 }}>
                <PersonLink contactId={contact.guardian_contact_id} name={contact.guardian_name}>
                  Open {contact.guardian_name}&apos;s profile →
                </PersonLink>
              </div>
            )}
            <EditableField testid="pp-guardian-rel" label="Guardian relation" value={contact.guardian_relation} type="select" options={guardianRelationOptions} onSave={(v) => patch({ guardian_relation: v })} />
          </div>

          <div className="pp-panel">
            <div className="pp-panel-head"><MapPin size={14} /> <b>Voter roll</b></div>
            <EditableField testid="pp-epic" label="EPIC / Voter ID" value={contact.epic} onSave={(v) => patch({ epic: v })} />
            <EditableField testid="pp-booth" label="Booth / Part No" value={contact.booth} onSave={(v) => patch({ booth: v })} />
            <EditableField testid="pp-epic-serial" label="EPIC (Voter) Serial" value={contact.epic_voter_serial} onSave={(v) => patch({ epic_voter_serial: v })} />
            <EditableField testid="pp-roll-page" label="Voter Roll Page No" value={contact.voter_roll_page_no} onSave={(v) => patch({ voter_roll_page_no: v })} />
            <EditableField testid="pp-ward" label="Ward" value={contact.ward} type="number" onSave={(v) => patch({ ward: v ? parseInt(v, 10) : null })} />
            <EditableField testid="pp-station" label="Polling station" value={contact.polling_station_name} onSave={(v) => patch({ polling_station_name: v })} />
          </div>

          <div className="pp-panel">
            <div className="pp-panel-head"><FileText size={14} /> <b>Political & health</b></div>
            <EditableField testid="pp-inclination" label="Inclination" value={contact.inclination} type="select" options={inclinationOptions} onSave={(v) => patch({ inclination: v })} />
            <EditableField testid="pp-status" label="Status" value={contact.status} type="select" options={statusOptions} onSave={(v) => patch({ status: v })} />
            <EditableField testid="pp-household" label="Household" value={householdName || ""} placeholder="Not linked" disabled />
            <EditableField testid="pp-notes" label="Notes" value={contact.notes} type="textarea" onSave={(v) => patch({ notes: v })} />
          </div>
        </section>
      )}

      {tab === "roll" && (
        <section className="pp-roll" data-testid="pp-roll">
          {pagesLoading ? (
            <p className="hint" style={{ padding: 24, textAlign: "center" }}>Loading roll pages…</p>
          ) : pages.length === 0 && !contact.voter_roll_page_file_id ? (
            <EmptyState icon={Layers} title="No voter roll pages linked" hint="Upload a voter roll and this person will get their page image here." />
          ) : (
            <div className="pp-roll-grid" data-testid="pp-roll-grid">
              {(pages.length > 0 ? pages : [{
                file_id: contact.voter_roll_page_file_id,
                page_no: contact.voter_roll_page_no,
                polling_station_name: contact.polling_station_name,
                roll_date: contact.voter_roll_date,
                booth: contact.booth,
                is_primary: true,
              }]).map((p) => (
                <article key={p.file_id} className={`pp-roll-card ${p.is_primary ? "primary" : ""}`} data-testid={`pp-roll-card-${p.file_id}`}>
                  <div className="pp-roll-thumb" onClick={() => setLightbox(p)}>
                    <img src={`/api/files/${p.file_id}`} alt={`Voter roll page ${p.page_no || ""}`} />
                    {p.is_primary && <span className="pp-roll-badge">Primary</span>}
                  </div>
                  <div className="pp-roll-meta">
                    <b>{p.polling_station_name || "Polling station"}</b>
                    <small>
                      {p.page_no ? `Page #${p.page_no}` : ""}{p.booth ? ` · Booth ${p.booth}` : ""}
                      {p.roll_date ? ` · ${p.roll_date}` : ""}
                    </small>
                  </div>
                  <div className="pp-roll-actions">
                    <button className="vr-btn" data-testid={`pp-roll-view-${p.file_id}`} onClick={() => setLightbox(p)}><Eye size={12} /> View</button>
                    <a className="vr-btn" data-testid={`pp-roll-save-${p.file_id}`} href={`/api/files/${p.file_id}`} download={`voter-roll-${contact.name}-p${p.page_no || ""}.png`}><Download size={12} /> Save</a>
                    <button
                      className="vr-btn"
                      data-testid={`pp-roll-print-${p.file_id}`}
                      onClick={() => {
                        const w = window.open("", "print");
                        if (!w) return;
                        w.document.write(`<html><head><title>Voter roll — ${contact.name}</title><style>body{margin:0}img{width:100%;height:auto}</style></head><body onload="window.print();window.close();"><img src="/api/files/${p.file_id}" /></body></html>`);
                        w.document.close();
                      }}
                    ><Printer size={12} /> Print</button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      )}

      {tab === "journey" && (
        <section className="pp-journey" data-testid="pp-journey">
          <VoterJourney contactId={contactId} user={user} inline />
        </section>
      )}

      {tab === "activity" && (
        <section className="pp-activity" data-testid="pp-activity">
          {timelineLoading ? (
            <p className="hint" style={{ padding: 24, textAlign: "center" }}>Loading activity…</p>
          ) : (
            <ActivityFeed
              contactName={contact.name}
              timeline={timeline}
              audit={audit}
              filter={activityFilter}
              onFilter={setActivityFilter}
            />
          )}
        </section>
      )}

      {lightbox && (
        <div className="modal-backdrop vr-lightbox" data-testid="pp-lightbox" onClick={(e) => { if (e.target === e.currentTarget) setLightbox(null); }}>
          <div className="vr-lightbox-panel">
            <div className="vr-lightbox-head">
              <b>Voter roll {lightbox.page_no ? `#${lightbox.page_no}` : ""} — {contact.name}</b>
              <button className="icon-button" onClick={() => setLightbox(null)}><X size={16} /></button>
            </div>
            <div className="vr-lightbox-body"><img src={`/api/files/${lightbox.file_id}`} alt="Voter roll" /></div>
          </div>
        </div>
      )}
    </>
  );
}
